import DOMPurify from "dompurify";

/**
 * Tags publisher supplied content is allowed to use inside offer/reward modals.
 * Anything else (script, iframe, object, embed, applet, form, input, button,
 * style, div, span, ...) is stripped while its text content is kept.
 */
const ALLOWED_TAGS = ["a", "b", "strong", "i", "em", "u", "br", "p", "ul", "ol", "li"];

const ALLOWED_ATTR = ["href", "target", "rel"];

/**
 * Only absolute http(s) links are considered safe.
 * Blocks javascript:, data:, file:, vbscript:, blob:, etc.
 */
const SAFE_URI_REGEXP = /^https?:\/\//i;

/**
 * Sanitizes publisher supplied HTML using the strict offerwall policy.
 * Prefer the `SafeHtml` component over calling `dangerouslySetInnerHTML` directly.
 */
export const purifyHtml = (html: string): string => {
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    FORBID_TAGS: [
      "script",
      "iframe",
      "object",
      "embed",
      "applet",
      "form",
      "input",
      "button",
      "style",
      "div",
      "span",
      "svg",
      "math",
    ],
    FORBID_ATTR: ["style", "class", "id", "src", "srcset", "formaction"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });

  const container = document.createElement("div");
  container.innerHTML = sanitizedHtml;

  container.querySelectorAll("a").forEach((link) => {
    const href = link.getAttribute("href") ?? "";

    if (!SAFE_URI_REGEXP.test(href)) {
      link.removeAttribute("href");
      link.removeAttribute("target");
      link.removeAttribute("rel");
      return;
    }

    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer nofollow");
  });

  return container.innerHTML;
};

/**
 * Strips every tag and returns the plain text content.
 * Useful for measuring/truncating content without breaking markup.
 */
export const purifyToText = (html: string): string => {
  const container = document.createElement("div");
  container.innerHTML = DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

  return container.textContent ?? "";
};

/**
 * Sanitizes HTML and truncates its text content without cutting markup in half.
 */
export const purifyAndTruncateHtml = (html: string, characterLimit: number): string => {
  const sanitizedHtml = purifyHtml(html);
  const source = document.createElement("div");
  source.innerHTML = sanitizedHtml;

  if ((source.textContent ?? "").length <= characterLimit) {
    return sanitizedHtml;
  }

  const target = document.createElement("div");
  let remainingCharacters = characterLimit;

  const appendTruncatedChildren = (sourceParent: Node, targetParent: Node) => {
    for (const child of Array.from(sourceParent.childNodes)) {
      if (remainingCharacters === 0) break;

      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? "";
        targetParent.appendChild(document.createTextNode(text.slice(0, remainingCharacters)));
        remainingCharacters -= Math.min(text.length, remainingCharacters);
        continue;
      }

      if (child.nodeType === Node.ELEMENT_NODE) {
        const clone = child.cloneNode(false);
        targetParent.appendChild(clone);
        appendTruncatedChildren(child, clone);
      }
    }
  };

  appendTruncatedChildren(source, target);
  target.append("...");

  return target.innerHTML;
};

export const PurifyText = (text: string) => DOMPurify.sanitize(text);
